import { AInput } from "@/admin/form/Fields.tsx";
import { updateAmountsMutation } from "@/admin/marketing/referralAmountsQuery.ts";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { siteCurrencyAtom } from "@/config.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { ReferralAmounts } from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai/react";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

interface ReferralAmountsModalProps {
    amounts: ReferralAmounts;
}

interface PaymentMethodButtonProps extends ReferralAmountsModalProps {
    button: ReactNode;
}

export function ReferralAmountsButton(props: PaymentMethodButtonProps) {
    return (
        <WModal button={props.button}>
            <ReferralAmountsModalContent {...props} />
        </WModal>
    );
}

interface ReferralAmountsInputs {
    promoter_credit: string;
    receiver_credit: string;
    receiver_precondition_topup_sum: string;
}

function ReferralAmountsModalContent(props: ReferralAmountsModalProps) {
    const { amounts } = props;
    //#region Hooks
    const modifyMutation = useStandardMutation(updateAmountsMutation);
    const siteCurrency = useAtomValue(siteCurrencyAtom);
    //#endregion

    const onSubmit: SubmitHandler<ReferralAmountsInputs> = async (inputs) => {
        const { promoter_credit, receiver_credit, receiver_precondition_topup_sum } = inputs;

        await modifyMutation.mutateAsync({
            body: {
                promoter_credit: promoter_credit ? Number(promoter_credit) : 0,
                receiver_credit: promoter_credit ? Number(receiver_credit) : 0,
                receiver_precondition_topup_sum: receiver_precondition_topup_sum
                    ? Number(receiver_precondition_topup_sum)
                    : 0,
            },
        });
    };

    const form = useForm<ReferralAmountsInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            promoter_credit: String(amounts.promoter_credit),
            receiver_credit: String(amounts.receiver_credit),
            receiver_precondition_topup_sum: String(amounts.receiver_precondition_topup_sum),
        },
    });

    const { control } = form;

    return (
        <WModalForm title="Update Referral Program Amounts" label="Update" form={form} onSubmit={onSubmit}>
            <Controller
                control={control}
                name="promoter_credit"
                render={({ field }) => (
                    <WTextField
                        autoFocus
                        headless
                        block
                        label={`Promoter credit amount (${siteCurrency})`}
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="receiver_credit"
                render={({ field }) => (
                    <WTextField
                        headless
                        block
                        label={`Receiver credit amount (${siteCurrency})`}
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="receiver_precondition_topup_sum"
                render={({ field }) => (
                    <WTextField
                        headless
                        block
                        label={`Receiver top up requirement (${siteCurrency})`}
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
